<template>
  <Component
    :is="tag"
    :type="type"
    :style="inheritFontSize && 'font-size: inherit'"
    :class="[
      'group relative font-normal transition-colors',
      computedClasses,
      { loading: loading, icon: icon || iconTransparent, active: active },
      {
        '!border-button-tertiary-active ': active && severity === 'tertiary'
      }
    ]"
    :disabled="disabled"
    @click="!loading && !disabled && emit('click', $event)"
  >
    <span v-if="loading" class="absolute inset-center">
      <UiIcon name="spin" class="size-6 animate-spin text-icon-tertiary" />
    </span>
    <slot />
  </Component>
</template>

<script lang="ts" setup>
import { type Component, computed } from 'vue'
import UiIcon from './UiIcon.vue'

type PropsType = {
  severity?: 'primary' | 'secondary' | 'tertiary' | 'quaternary'
  size?: 'big' | 'small'
  type?: 'button' | 'submit' | 'reset'
  loading?: boolean
  icon?: boolean
  iconTransparent?: boolean
  disabled?: boolean
  tag?: string | Component
  active?: boolean
  inheritFontSize?: boolean
}

type EmitType = {
  (e: 'click', event: MouseEvent): void
}

const props = withDefaults(defineProps<PropsType>(), {
  severity: 'primary',
  size: 'big',
  type: 'button',
  transparent: false,
  disabled: false,
  tag: 'button',
  iconTransparent: false,
  active: false,
  inheritFontSize: false
})
const emit = defineEmits<EmitType>()

const severityClassesMap: Record<NonNullable<PropsType['severity']>, string> = {
  primary: [
    'text-button-primary rounded-lg bg-button-primary border border-button-primary ',
    'hover:text-button-primary-hover hover:bg-button-primary-hover hover:border-button-primary-hover ',
    'active:text-button-primary-active active:bg-button-primary-active active:border-button-primary-active',
    'focus-visible:text-button-primary-focus focus-visible:bg-button-primary-focus focus-visible:border-button-primary-focus'
  ].join(' '),
  secondary: [
    'text-button-secondary font-medium !rounded-full bg-button-secondary border border-button-secondary',
    'hover:text-button-secondary-hover hover:bg-button-secondary-hover hover:border-button-secondary-hover',
    'active:text-button-secondary-active active:bg-button-secondary-active active:border-button-secondary-active',
    'focus-visible:text-button-secondary-focus focus-visible:bg-button-secondary-focus focus-visible:border-button-secondary-focus'
  ].join(' '),
  tertiary: [
    '!rounded-full text-button-tertiary bg-button-tertiary border border-button-tertiary',
    'hover:text-button-tertiary-hover hover:bg-button-tertiary-hover hover:border-button-tertiary-hover',
    'active:text-button-tertiary-active primary-bg-active active:border-button-tertiary-active',
    'focus-visible:text-button-tertiary-focus focus-visible:bg-button-tertiary-focus focus-visible:border-button-tertiary-focus'
  ].join(' '),
  quaternary: [
    'text-button-quaternary !rounded-full bg-button-quaternary border border-button-quaternary ',
    'hover:text-button-quaternary-hover hover:bg-button-quaternary-hover hover:border-button-quaternary-hover ',
    'active:text-button-quaternary-active active:bg-button-quaternary-active active:border-button-quaternary-active ',
    'focus-visible:text-button-quaternary-focus focus-visible:bg-button-quaternary-focus focus-visible:border-button-quaternary-focus '
  ].join(' ')
}

const sizeClassesMap: Record<NonNullable<PropsType['size']>, string> = {
  big: 'md:rounded-lg  ',
  small: 'text-xs'
}

const defaultLoadingClasses = '!text-transparent !bg-transparent !border-transparent'
const loadingClassesMap: Record<NonNullable<PropsType['severity']>, string> = {
  primary: `${defaultLoadingClasses}`,
  secondary: `${defaultLoadingClasses}`,
  tertiary: `${defaultLoadingClasses} `,
  quaternary: `${defaultLoadingClasses} `
}

const disabledClassesMap: Record<NonNullable<PropsType['severity']>, string> = {
  primary:
    'disabled:text-button-primary-disabled disabled:!bg-button-primary-disabled disabled:border-button-primary-disabled',
  secondary:
    'disabled:text-button-secondary-disabled disabled:bg-button-secondary-disabled disabled:border-button-secondary-disabled',
  tertiary:
    'disabled:text-button-tertiary-disabled disabled:bg-button-tertiary-disabled disabled:border-button-tertiary-disabled',
  quaternary:
    'disabled:text-button-quaternary-disabled disabled:bg-button-quaternary-disabled disabled:border-button-quaternary-disabled '
}

const defaultIconClasses = [
  '!rounded-full !border w-fit ',
  'hover:!bg-button-icon-hover',
  ' active:border-button-icon-active',
  ' focus-visible:border-button-icon-focus',
  ' disabled:bg-button-icon-disabled'
].join(' ')
const iconClassesMap: Record<NonNullable<PropsType['size']>, string> = {
  big: `${defaultIconClasses} !p-3 !border-button-icon !bg-button-icon disabled:!bg-button-icon-disabled focus-visible:!bg-button-icon-focus active:!bg-button-icon-active  hover:!border-button-icon-hover`,
  small: `${defaultIconClasses} !p-2 !border-transparent !bg-button-icon disabled:!bg-button-icon-disabled focus-visible:!bg-button-icon-focus active:!bg-button-icon-active  hover:!border-button-icon-hover`
}
const iconTransparentClassesMap: Record<NonNullable<PropsType['size']>, string> = {
  big: `${defaultIconClasses} !p-0 !bg-transparent focus-visible:!border-transparent active:!border-transparent hover:!border-transparent focus-visible:!bg-transparent active:!bg-transparent hover:!bg-transparent !border-transparent `,
  small: `${defaultIconClasses} !p-0 !bg-transparent !border-transparent focus-visible:!bg-transparent active:!bg-transparent hover:!bg-transparent focus-visible:!border-transparent active:!border-transparent hover:!border-transparent`
}

const computedClasses = computed(() =>
  [
    severityClassesMap[props.severity],
    sizeClassesMap[props.size],
    props.loading ? loadingClassesMap[props.severity] : '',
    props.icon ? iconClassesMap[props.size] : '',
    props.iconTransparent ? iconTransparentClassesMap[props.size] : '',
    props.disabled ? disabledClassesMap[props.severity] : ''
  ].join(' ')
)
</script>

<style scoped>
.primary-bg-active:active {
  background-image: var(--background-button-primary-active);
}

.primary-bg-active:disabled,
.primary-bg-active.loading,
.primary-bg-active.text,
.primary-bg-active.icon {
  background-image: none;
}
</style>
